// "Universal Translator" gimmick — see UniversalTranslatorButton.jsx.
//
// Three engines, tried in order, each only handling what the previous one
// couldn't:
//   1. Chrome/Edge's on-device Translator API (window.Translator) when the
//      browser already has the language pair's model ready — unlimited,
//      completely local, nothing leaves the visitor's machine.
//   2. DeepL, via our own backend (api/routes/translate.js) — the key is a
//      secret and must never reach the browser. Best quality, a real monthly
//      quota shared across every visitor (cached server-side too).
//   3. MyMemory's free public API, called directly from the browser — no
//      signup, no key, but a small per-visitor daily quota and lower quality.
//      Last resort, and only for whatever DeepL didn't handle.
//
// This never touches Scrivito content: it walks the already-rendered DOM and
// writes translations straight into text nodes. Reverting restores the exact
// original strings captured on first use.

export const LANGUAGES = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pl', label: 'Polski' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ru', label: 'Русский' },
  { code: 'uk', label: 'Українська' },
  { code: 'ja', label: '日本語' },
  { code: 'zh', label: '中文' },
  { code: 'ko', label: '한국어' },
  { code: 'ar', label: 'العربية' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'sv', label: 'Svenska' },
  { code: 'cs', label: 'Čeština' },
];

// Elements the scan must never rewrite: the widget's own UI (so its button
// never translates itself into something unreadable), and the split-flap
// boards (already a multilingual showcase; re-translating "TRANSLATION"
// mid-flip would just fight the component's own interval).
const SKIP_SELECTOR = 'script, style, noscript, [data-no-translate], .tt-flap, .tt-translate-word';

export function collectTextNodes(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent || parent.closest(SKIP_SELECTOR)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  let n = walker.nextNode();
  while (n) {
    nodes.push(n);
    n = walker.nextNode();
  }
  return nodes;
}

function detectSourceLang() {
  return (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
}

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const FLAP_HEADING_SELECTOR = '.tt-hero__title, .center-heading h2, .tt-services__head h2';

// The flip rebuilds a heading as many small spans (see flipReplaceElement
// below), which would otherwise fragment into dozens of single-character
// "unique strings" on a later scan — and since the original text node it
// replaced is now detached from the DOM, reverting couldn't find its way
// back either. Both problems go away by treating these headings as
// self-contained cases: each keeps its true source text in a
// `data-tt-original` attribute (set once, the first time it's ever
// touched), and every activation resets them back to that plain text
// *before* scanning, so the rest of the pipeline always sees one ordinary
// text node there, exactly like anywhere else on the page.
function resetFlapHeadingsToPlainText() {
  document.querySelectorAll(FLAP_HEADING_SELECTOR).forEach((el) => {
    if (el.dataset.ttOriginal === undefined) {
      el.dataset.ttOriginal = el.textContent;
    } else {
      el.textContent = el.dataset.ttOriginal;
    }
  });
}

const SCRAMBLE_CHARS = '#*+ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

// Cycles a single character span through a couple of random glyphs before
// landing on its real one, re-triggering the same short flip keyframe each
// step — the same thing a real split-flap board does mechanically (spinning
// through several flaps before settling), just compressed into a few
// hundred milliseconds.
function cycleFlapChar(span, finalChar, startDelay) {
  // Respect the visitor's reduced-motion preference: land on the final
  // character immediately instead of spinning through random glyphs.
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    span.textContent = finalChar === ' ' ? '\u00A0' : finalChar;
    return;
  }
  const steps = 2 + Math.floor(Math.random() * 2); // 2–3 scramble steps
  let step = 0;

  const render = (ch) => {
    span.textContent = ch === ' ' ? '\u00A0' : ch;
    span.classList.remove('tt-translate-char--flip');
    // eslint-disable-next-line no-unused-expressions
    span.offsetWidth; // restart the animation
    span.classList.add('tt-translate-char--flip');
  };

  const tick = () => {
    if (step < steps) {
      render(SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]);
      step += 1;
      setTimeout(tick, 70 + Math.random() * 40);
    } else {
      render(finalChar);
    }
  };

  setTimeout(tick, startDelay);
}

// A showpiece flourish for the hero and section headlines: rebuilds the
// heading as one animated span per character and cycles each through a
// couple of random glyphs before landing on the real one, staggered left to
// right — visually the same "several flaps spinning before landing" read as
// the split-flap board itself (main.scss's .tt-flap), just applied to
// running text instead of a fixed-width board.
function flipReplaceElement(el, text) {
  const frag = document.createDocumentFragment();
  let delayIndex = 0;
  // Adjacent inline-block boxes are each their own wrap-boundary by default —
  // one span per *character* with no grouping let the browser break between
  // any two letters, not just at real spaces. Each word's letters are
  // grouped into one `white-space: nowrap` wrapper so the word wraps as a
  // single unit, exactly like normal text; only the plain space text nodes
  // between word-wrappers are valid break points.
  text.split(' ').forEach((word, i, words) => {
    if (word.length > 0) {
      const wordSpan = document.createElement('span');
      wordSpan.className = 'tt-translate-word';
      Array.from(word).forEach((ch) => {
        const charSpan = document.createElement('span');
        charSpan.className = 'tt-translate-char';
        charSpan.textContent = '\u00A0';
        wordSpan.appendChild(charSpan);
        cycleFlapChar(charSpan, ch, delayIndex * 40);
        delayIndex += 1;
      });
      frag.appendChild(wordSpan);
    }
    if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
  });
  el.textContent = '';
  el.appendChild(frag);
}

function flashElement(el) {
  el.classList.remove('tt-translate-flash');
  // Force reflow so the class can be re-added to replay the animation on a
  // node that was already flashed earlier in this same run.
  // eslint-disable-next-line no-unused-expressions
  el.offsetWidth;
  el.classList.add('tt-translate-flash');
  el.addEventListener('animationend', () => el.classList.remove('tt-translate-flash'), { once: true });
}

function applyTranslation(node, text) {
  const parent = node.parentElement;
  if (parent && parent.matches(FLAP_HEADING_SELECTOR)) {
    flipReplaceElement(parent, text);
  } else {
    node.nodeValue = text;
    if (parent) flashElement(parent);
  }
}

// ── Engine 1: Chrome/Edge on-device Translator ──────────────────────────────
// Only used when the model is already downloaded and `available` — a
// `downloadable`/`downloading` state can take real time, which would turn a
// two-second gimmick into an unexplained multi-second (or longer) stall on
// first use. Anything less than instantly ready falls through instead.
async function getOnDeviceTranslator(sourceLang, targetLang) {
  if (sourceLang === targetLang) return null;
  if (typeof self === 'undefined' || !('Translator' in self)) return null;
  try {
    const availability = await self.Translator.availability({ sourceLanguage: sourceLang, targetLanguage: targetLang });
    if (availability === 'available') {
      return await self.Translator.create({ sourceLanguage: sourceLang, targetLanguage: targetLang });
    }
    if (availability === 'downloadable') {
      // Kick off the download in the background (not awaited — this run
      // still falls through to DeepL/MyMemory) so a later click, or the next
      // visit once Chrome has cached the model, gets the free/unlimited
      // on-device engine instead.
      self.Translator.create({ sourceLanguage: sourceLang, targetLanguage: targetLang }).catch(() => {});
    }
    return null;
  } catch {
    return null;
  }
}

// ── Engine 2: DeepL (via our backend — the key is a secret) ────────────────
const DEEPL_BATCH_SIZE = 100; // DeepL accepts up to 128 texts per request

async function deeplTranslateBatch(texts, targetLang) {
  const res = await fetch('/api/translate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ texts, lang: targetLang }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'deepl_failed');
    err.code = data.error || 'deepl_failed';
    throw err;
  }
  return data.translations;
}

// ── Engine 3: MyMemory (free, no key, called straight from the browser) ────
const MYMEMORY_MAX_BYTES = 480; // MyMemory hard-caps `q` at 500 bytes

function byteLength(str) {
  return new TextEncoder().encode(str).length;
}

// Long paragraphs (a service description, a testimonial) need splitting into
// pieces MyMemory will accept; sentence boundaries keep each piece coherent
// for the translator, with a hard character slice as a last resort.
function splitForMyMemory(text) {
  if (byteLength(text) <= MYMEMORY_MAX_BYTES) return [text];
  const sentences = text.match(/[^.!?]+[.!?]*\s*/g) || [text];
  const chunks = [];
  let current = '';
  for (const sentence of sentences) {
    if (byteLength(current + sentence) > MYMEMORY_MAX_BYTES) {
      if (current) chunks.push(current);
      current = sentence;
      while (byteLength(current) > MYMEMORY_MAX_BYTES) {
        let cut = current.length;
        while (cut > 0 && byteLength(current.slice(0, cut)) > MYMEMORY_MAX_BYTES) cut -= 10;
        chunks.push(current.slice(0, cut));
        current = current.slice(cut);
      }
    } else {
      current += sentence;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

async function myMemoryTranslateOne(text, sourceLang, targetLang) {
  const parts = splitForMyMemory(text);
  const translatedParts = [];
  for (const part of parts) {
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(part)}&langpair=${sourceLang}|${targetLang}`;
    const res = await fetch(url);
    if (res.status === 429) {
      const err = new Error('mymemory_quota');
      err.code = 'mymemory_quota';
      throw err;
    }
    if (!res.ok) {
      const err = new Error('mymemory_request_failed');
      err.code = 'mymemory_request_failed';
      throw err;
    }
    const data = await res.json();
    const translated = data?.responseData?.translatedText;
    // Over-quota responses can also arrive as HTTP 200 with a warning string
    // standing in for the translation instead of a real error field.
    if (data.quotaFinished || (typeof translated === 'string' && translated.startsWith('MYMEMORY WARNING'))) {
      const err = new Error('mymemory_quota');
      err.code = 'mymemory_quota';
      throw err;
    }
    if (typeof translated !== 'string' || !translated) {
      const err = new Error('mymemory_bad_response');
      err.code = 'mymemory_bad_response';
      throw err;
    }
    translatedParts.push(translated);
  }
  return translatedParts.join(' ');
}

async function runWithConcurrency(items, worker, concurrency) {
  let index = 0;
  async function lane() {
    for (;;) {
      const i = index++;
      if (i >= items.length) return;
      await worker(items[i]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, lane));
}

// ── Persistent, text-keyed cache (survives reloads and repeat visits) ──────
// Keyed by the source text itself (not by DOM node, which doesn't survive a
// reload) — this is also what makes de-duplication possible: this page
// repeats plenty of strings verbatim ("Angebot anfordern" alone appears in
// the hero and in every one of the 20 service cards), so translating each
// *unique* string once and fanning the result out to every matching node
// cuts real request/character volume substantially, on top of never paying
// for the same string twice across a reload or a later visit.
const STORAGE_KEY = 'tt_ut_cache_v1';

function loadPersistedCache() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function savePersistedCache(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage full or unavailable (private browsing) — the run already has
    // its results applied in memory, so this is a missed optimisation, not
    // a failure.
  }
}

// Runs the whole activation: scans the page, groups nodes by their exact
// source text, and translates each *unique* string once — applying the
// result to every node that repeats it, then hands whatever's left down the
// engine chain (on-device → DeepL → MyMemory). `originals` is a Map the
// caller keeps across activations so reverting (or re-translating into a
// different language) always starts from the true source text. Results are
// also kept in a `localStorage`-backed cache keyed by text+language, so a
// reload or a later visit never pays for the same string twice.
export async function activateTranslation({ lang, originals, onProgress }) {
  resetFlapHeadingsToPlainText();
  const nodes = collectTextNodes(document.body);
  const sourceLang = detectSourceLang();
  const total = nodes.length;
  let settled = 0;

  nodes.forEach((node) => {
    if (!originals.has(node)) originals.set(node, node.nodeValue);
  });

  const store = loadPersistedCache();
  const cacheForLang = store[lang] || {};

  const textToNodes = new Map();
  for (const node of nodes) {
    const text = originals.get(node);
    if (!textToNodes.has(text)) textToNodes.set(text, []);
    textToNodes.get(text).push(node);
  }

  const applyToGroup = (text, translated) => {
    for (const node of textToNodes.get(text)) applyTranslation(node, translated);
  };

  let dirty = false;
  const commit = (text, translated) => {
    cacheForLang[text] = translated;
    dirty = true;
    applyToGroup(text, translated);
    settled += textToNodes.get(text).length;
    onProgress?.({ done: settled, total });
  };

  let remaining = [];
  for (const text of textToNodes.keys()) {
    const cached = cacheForLang[text];
    if (cached !== undefined) {
      applyToGroup(text, cached);
      settled += textToNodes.get(text).length;
    } else {
      remaining.push(text);
    }
  }
  onProgress?.({ done: settled, total });

  if (remaining.length === 0) return { partial: false, engine: 'cache' };

  let engine = null;
  let lastError = null;

  // Engine 1: on-device.
  const onDevice = await getOnDeviceTranslator(sourceLang, lang);
  if (onDevice) {
    const stillRemaining = [];
    for (const text of remaining) {
      try {
        const translated = await onDevice.translate(text);
        commit(text, translated);
        engine = 'on-device';
      } catch (err) {
        lastError = err;
        stillRemaining.push(text);
      }
    }
    onDevice.destroy();
    remaining = stillRemaining;
  }

  // Engine 2: DeepL — batched (one request can carry every remaining unique
  // string), and a whole-engine failure (not configured, quota exceeded,
  // network down) just leaves its batch in `remaining` for MyMemory.
  if (remaining.length > 0) {
    const stillRemaining = [];
    for (const batch of chunkArray(remaining, DEEPL_BATCH_SIZE)) {
      try {
        const translations = await deeplTranslateBatch(batch, lang);
        batch.forEach((text, i) => {
          commit(text, translations[i]);
          engine = 'deepl';
        });
      } catch (err) {
        lastError = err;
        stillRemaining.push(...batch);
      }
    }
    remaining = stillRemaining;
  }

  // Engine 3: MyMemory — last resort, one request per string.
  if (remaining.length > 0) {
    await runWithConcurrency(
      remaining,
      async (text) => {
        try {
          const translated = await myMemoryTranslateOne(text, sourceLang, lang);
          commit(text, translated);
          engine = engine || 'mymemory';
        } catch (err) {
          lastError = err;
        }
      },
      6
    );
  }

  if (dirty) {
    store[lang] = cacheForLang;
    savePersistedCache(store);
  }

  if (!engine && lastError) throw lastError;
  return { partial: settled < total, engine: engine || 'cache' };
}

export function revertTranslation(originals) {
  // A flap heading's text node may have been replaced (and detached) by the
  // flip effect, so each is restored directly from its own stored original
  // rather than through the generic per-node loop below.
  document.querySelectorAll(FLAP_HEADING_SELECTOR).forEach((el) => {
    if (el.dataset.ttOriginal !== undefined) el.textContent = el.dataset.ttOriginal;
  });
  for (const [node, original] of originals) {
    if (node.isConnected) node.nodeValue = original;
  }
}
